import type {EntradasDeCartaDeInstruccion, ResultadoDeGeneracion} from '@cnc/contracts';
import {useEffect, useState} from 'react';
import {ErrorDeApi} from '../api/cliente';
import {rutaDeDocumentoGenerado, useCartaDeInstruccion, useGenerarCartaDeInstruccion} from '../api/documentos';
import {tr} from '../i18n/idioma';
import {fecha as formatoFecha, fechaHora, numero} from '../utilidades/formato';
import {usePermisos} from './sesion-contexto';

const ONZA_TROY_G = 31.1034768;
const boton =
  'rounded-md bg-verde px-3 py-1.5 text-sm font-medium text-white hover:bg-verde-oscuro disabled:opacity-50';
const campo = 'mt-1 w-full rounded-md border border-linea px-2 py-1.5 text-sm text-tinta';

// Carta de instruccion del remitente a Brink's (SLI, Daniel 24/10/2026): se
// hace antes de llenar el contenedor, para MM, CM, IPM y MA. Se escriben la
// fecha y los precios del oro y la plata; lo demas sale de la exportacion y
// de la plantilla del exportador (su ultimo SLI, en su ficha de Empresas).
export function CartaDeInstruccion({codigo}: {codigo: string}) {
  const permisos = usePermisos();
  const estado = useCartaDeInstruccion(codigo);
  const generar = useGenerarCartaDeInstruccion(codigo);
  const [fecha, setFecha] = useState('');
  const [precioOro, setPrecioOro] = useState('');
  const [precioPlata, setPrecioPlata] = useState('');
  const [fechaExportacion, setFechaExportacion] = useState('');
  const [piezas, setPiezas] = useState('');
  const [peso, setPeso] = useState('');
  const [oro, setOro] = useState('');
  const [plata, setPlata] = useState('');
  const [resultado, setResultado] = useState<ResultadoDeGeneracion | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Valores iniciales: hoy, los precios de la ultima carta y lo de la exportacion.
  const e = estado.data;
  useEffect(() => {
    if (!e) return;
    setFecha(f => f || e.hoy);
    setPrecioOro(p => p || (e.ultima ? String(e.ultima.precioOro) : ''));
    setPrecioPlata(p => p || (e.ultima ? String(e.ultima.precioPlata) : ''));
    setFechaExportacion(f => f || (e.fechaExportacion ?? ''));
    setPiezas(p => p || (e.piezas ? String(e.piezas) : ''));
    setPeso(p => p || (e.pesoHumedoKg ? String(Math.round(e.pesoHumedoKg * 100) / 100) : ''));
    setOro(p => p || (e.oroG ? String(e.oroG) : ''));
    setPlata(p => p || (e.plataG ? String(e.plataG) : ''));
  }, [e]);

  if (estado.isError) return null;
  if (!e) return null;
  const n = (v: string) => Number(v.replace(',', '.'));
  const oroG = e.conBigBags ? e.oroG : n(oro) || 0;
  const plataG = e.conBigBags ? e.plataG : n(plata) || 0;
  const valor =
    n(precioOro) > 0 && n(precioPlata) > 0
      ? (oroG / ONZA_TROY_G) * n(precioOro) + (plataG / ONZA_TROY_G) * n(precioPlata)
      : null;

  const enviar = async (ev: React.FormEvent) => {
    ev.preventDefault();
    setError(null);
    const entradas: EntradasDeCartaDeInstruccion = {
      fecha,
      precioOro: n(precioOro),
      precioPlata: n(precioPlata),
      fechaExportacion: fechaExportacion || null,
      ...(e.conBigBags ? {} : {piezas: n(piezas), pesoHumedoKg: n(peso), oroG: n(oro), plataG: n(plata)}),
    };
    try {
      setResultado(await generar.mutateAsync(entradas));
    } catch (err) {
      setError(
        err instanceof ErrorDeApi
          ? [err.message, ...(err.detalles ?? [])].join(' ')
          : tr('No se pudo generar.', 'Could not generate.'),
      );
    }
  };

  return (
    <div className="rounded-lg border border-linea bg-white p-5" data-prueba="carta-de-instruccion">
      <h2 className="font-medium">
        {tr('Carta de instrucción a Brink’s (SLI)', 'Letter of instruction to Brink’s (SLI)')}
      </h2>
      <p className="mt-1 text-xs text-apagado">
        {tr(
          'Se hace antes de llenar el contenedor. Sale sobre el último SLI del exportador: la app pone la fecha, la fecha de exportación, las piezas, el peso y el valor declarado (onzas de oro y plata a los precios que escribas).',
          "It is done before filling the container. It comes out on the exporter's latest SLI: the app puts the date, export date, pieces, weight and declared value (gold and silver ounces at the prices you type).",
        )}
      </p>
      {!e.plantilla && (
        <p className="mt-3 rounded-md border border-dorado/50 bg-dorado-claro px-3 py-2 text-xs">
          ⚠{' '}
          {tr(
            `Falta la plantilla del SLI de ${e.exportador ?? 'este exportador'}: sube su último SLI en Excel en Empresas → su ficha → Documentos de la empresa.`,
            `The SLI template of ${e.exportador ?? 'this exporter'} is missing: upload its latest SLI in Excel in Companies → its record → Company documents.`,
          )}
        </p>
      )}
      {e.plantilla && (
        <p className="mt-2 text-xs text-apagado">
          {e.plantilla.empresa
            ? tr(`Plantilla de ${e.plantilla.empresa}.`, `${e.plantilla.empresa} template.`)
            : tr('Plantilla general.', 'General template.')}
        </p>
      )}
      {permisos.editar && (
        <form
          onSubmit={ev => void enviar(ev)}
          className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4"
          aria-label={tr('Carta de instrucción', 'Letter of instruction')}
        >
          <label className="text-xs text-apagado">
            {tr('Fecha del documento', 'Document date')}
            <input type="date" required value={fecha} onChange={x => setFecha(x.target.value)} className={campo} />
          </label>
          <label className="text-xs text-apagado">
            {tr('Precio del oro (USD/oz)', 'Gold price (USD/oz)')}
            <input
              inputMode="decimal"
              required
              value={precioOro}
              onChange={x => setPrecioOro(x.target.value)}
              className={campo}
            />
          </label>
          <label className="text-xs text-apagado">
            {tr('Precio de la plata (USD/oz)', 'Silver price (USD/oz)')}
            <input
              inputMode="decimal"
              required
              value={precioPlata}
              onChange={x => setPrecioPlata(x.target.value)}
              className={campo}
            />
          </label>
          <label className="text-xs text-apagado">
            {tr('Fecha de exportación (ETD)', 'Export date (ETD)')}
            <input
              type="date"
              value={fechaExportacion}
              onChange={x => setFechaExportacion(x.target.value)}
              className={campo}
            />
          </label>
          {!e.conBigBags && (
            <>
              <p className="text-xs text-apagado sm:col-span-2 lg:col-span-4">
                {tr(
                  'Esta exportación no tiene Big Bags en la app: escribe las piezas, el peso y los gramos que van en la carta.',
                  'This export has no Big Bags in the app: type the pieces, weight and grams that go on the letter.',
                )}
              </p>
              <label className="text-xs text-apagado">
                {tr('Piezas (Big Bags)', 'Pieces (Big Bags)')}
                <input
                  inputMode="numeric"
                  required
                  value={piezas}
                  onChange={x => setPiezas(x.target.value)}
                  className={campo}
                />
              </label>
              <label className="text-xs text-apagado">
                {tr('Peso húmedo (kg)', 'Wet weight (kg)')}
                <input
                  inputMode="decimal"
                  required
                  value={peso}
                  onChange={x => setPeso(x.target.value)}
                  className={campo}
                />
              </label>
              <label className="text-xs text-apagado">
                {tr('Oro (g)', 'Gold (g)')}
                <input
                  inputMode="decimal"
                  required
                  value={oro}
                  onChange={x => setOro(x.target.value)}
                  className={campo}
                />
              </label>
              <label className="text-xs text-apagado">
                {tr('Plata (g)', 'Silver (g)')}
                <input inputMode="decimal" value={plata} onChange={x => setPlata(x.target.value)} className={campo} />
              </label>
            </>
          )}
          <div className="flex flex-wrap items-center gap-3 sm:col-span-2 lg:col-span-4">
            {e.conBigBags && (
              <span className="text-xs text-apagado">
                {tr(
                  `${numero(e.piezas)} Big Bags · ${numero(e.pesoHumedoKg, 2)} kg húmedos · ${numero(e.oroG, 2)} g de oro · ${numero(e.plataG, 2)} g de plata`,
                  `${numero(e.piezas)} Big Bags · ${numero(e.pesoHumedoKg, 2)} wet kg · ${numero(e.oroG, 2)} g gold · ${numero(e.plataG, 2)} g silver`,
                )}
              </span>
            )}
            {valor !== null && (
              <span className="text-sm">
                {tr('Valor declarado:', 'Declared value:')} <strong>USD {numero(valor, 2)}</strong>
              </span>
            )}
            <button type="submit" className={boton} disabled={generar.isPending || !e.plantilla}>
              {generar.isPending ? tr('Generando…', 'Generating…') : `↓ ${tr('Generar SLI', 'Generate SLI')}`}
            </button>
          </div>
        </form>
      )}
      {error && <p className="mt-3 text-sm text-alerta">{error}</p>}
      {resultado && (
        <div className="mt-3 rounded-md border border-verde/40 bg-verde-claro px-3 py-2 text-sm" role="status">
          {resultado.documentos.map(d => (
            <a key={d.id} href={rutaDeDocumentoGenerado(d.id)} className="text-verde hover:underline" download>
              ↓ {d.nombreArchivo}
            </a>
          ))}
          {resultado.avisos.map(a => (
            <p key={a} className="text-xs">
              ⚠ {a}
            </p>
          ))}
        </div>
      )}
      {e.generados.length > 0 && (
        <ul className="mt-3 space-y-1 text-xs text-apagado">
          {e.generados.slice(0, 5).map(g => (
            <li key={g.id}>
              <a href={rutaDeDocumentoGenerado(g.id)} className="text-verde hover:underline" download>
                ↓ {g.nombreArchivo}
              </a>{' '}
              · {fechaHora(g.generadoEl)}
              {typeof g.entradas.fecha === 'string' && ` · ${tr('fecha', 'date')} ${formatoFecha(g.entradas.fecha)}`}
              {g.generadoPor && ` · ${g.generadoPor}`}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
